import { useCallback, useEffect, useRef, useState } from 'react';
import { useMediaDevices, useToggle } from 'react-use';
import ZoomVideo, { CRCProtocol, LocalAudioTrack, TestMicrophoneReturn } from '@zoom/videosdk';

import { notifyError } from 'shared/Toast/Toast';

import { useQuery, useZoomCall } from 'hooks';
import { useAnalytics } from 'hooks/useAnalytics';
import { useRouteMatch } from 'hooks/useRouteMatch';
import { useWidth } from 'hooks/useWidth';
import { PathName } from 'router/paths';

import { filterDevicesByKind } from './settings.settings';

import sound from 'assets/sound_test.mp3';

/**
 * Manages Zoom device settings state and behaviors for audio/video selection and testing.
 * @returns Audio/video device lists, volume meter state, and handlers for settings actions.
 */
export const useSettingsHook = () => {
  const { isMobile } = useWidth();
  const logEvent = useAnalytics();

  const {
    callOutParticipant,
    cameraId,
    isOpenSettings,
    languageRequested,
    microphoneId,
    room,
    setCallOutParticipant,
    setCameraId,
    setLanguageRequested,
    setMicrophoneId,
    setSpeakerId,
    speakerId,
    toggleIsOpenRequestInterpreter,
    toggleIsOpenSettings
  } = useZoomCall();

  const isReschedule = useQuery().get('reschedule') === 'true';

  const isZoomCall = useRouteMatch(PathName.ZoomCall)?.isExact;
  const mediaDevices = useMediaDevices();
  const stream = room?.getMediaStream();

  const [disabledAudioButton, toggleDisabledAudioButton] = useToggle(false);
  const [volume, setVolume] = useState(0);
  const [microphoneTester, setMicrophoneTester] = useState<TestMicrophoneReturn>();
  const [audioTrack, setAudioTrack] = useState<LocalAudioTrack | null>(null);
  const [audioInputsDevices, setAudioInputsDevices] = useState<MediaDeviceInfo[]>([]);
  const [audioOutputsDevices, setAudioOutputsDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);

  const handlePlayAudio = () => {
    logEvent('call_settings_test_sound_btn_click');
    toggleDisabledAudioButton();
    new Audio(sound).play();
    setTimeout(toggleDisabledAudioButton, 3000);
  };

  const manualSelectionRef = useRef({ audioInput: false, audioOutput: false, video: false });

  const switchDevice = (
    deviceId: string,
    type: 'audioInput' | 'audioOutput' | 'video',
    isManual = false
  ) => {
    if (isManual && type === 'video') {
      const selectedCameraName =
        videoDevices.find((device) => device.deviceId === deviceId)?.label || deviceId;
      logEvent('call_settings_camera_selected', {
        device_name: selectedCameraName
      });
    }

    if (isManual && type === 'audioInput') {
      const selectedAudioInputName =
        audioInputsDevices.find((device) => device.deviceId === deviceId)?.label || deviceId;
      logEvent('call_settings_audio_input_selected', {
        device_name: selectedAudioInputName
      });
    }

    if (isManual && type === 'audioOutput') {
      const selectedAudioOutputName =
        audioOutputsDevices.find((device) => device.deviceId === deviceId)?.label || deviceId;
      logEvent('call_settings_audio_output_selected', {
        device_name: selectedAudioOutputName
      });
    }

    if (isManual) {
      manualSelectionRef.current = {
        ...manualSelectionRef.current,
        [type]: true
      };
    }

    const switchFn =
      type === 'audioInput'
        ? stream?.switchMicrophone(deviceId)
        : type === 'audioOutput'
          ? stream?.switchSpeaker(deviceId)
          : stream?.switchCamera(deviceId);
    const setFn =
      type === 'audioInput' ? setMicrophoneId : type === 'audioOutput' ? setSpeakerId : setCameraId;

    if (!switchFn) {
      setFn(deviceId);
      return;
    }

    switchFn.then(() => setFn(deviceId)).catch((err) => notifyError(err.reason));
  };

  /**
   * Synchronizes available Zoom devices, honoring manual selections when present,
   * otherwise choosing the default device by matching groupId.
   * @returns Promise that resolves after device lists and selections are updated.
   */
  const handleUpdatedDevices = useCallback(async () => {
    const devices = await ZoomVideo.getDevices();
    const audioInputs = filterDevicesByKind(devices, 'audioinput');
    const audioOutputs = filterDevicesByKind(devices, 'audiooutput');
    const videoInputs = filterDevicesByKind(devices, 'videoinput');

    if (audioOutputs.length === 0) {
      audioOutputs.push({
        deviceId: '',
        kind: 'audiooutput',
        label: 'Default output'
      } as MediaDeviceInfo);
    }

    const pickDevice = (
      preferredId: string | undefined,
      inputs: MediaDeviceInfo[],
      kind: MediaDeviceKind,
      isManualSelected: boolean
    ) => {
      if (
        isManualSelected &&
        preferredId &&
        inputs.some(({ deviceId }) => deviceId === preferredId)
      )
        return preferredId;
      const defaultGroupId = devices.find(
        (device) => device.deviceId === 'default' && device.kind === kind
      )?.groupId;

      return defaultGroupId
        ? inputs.find(({ groupId }) => groupId === defaultGroupId)?.deviceId
        : inputs[0]?.deviceId;
    };

    const newCameraId = pickDevice(
      cameraId,
      videoInputs,
      'videoinput',
      manualSelectionRef.current.video
    );
    if (newCameraId && newCameraId !== cameraId) switchDevice(newCameraId, 'video');

    const newMicrophoneId = pickDevice(
      microphoneId,
      audioInputs,
      'audioinput',
      manualSelectionRef.current.audioInput
    );
    if (newMicrophoneId && newMicrophoneId !== microphoneId)
      switchDevice(newMicrophoneId, 'audioInput');

    const newSpeakerId = pickDevice(
      speakerId,
      audioOutputs,
      'audiooutput',
      manualSelectionRef.current.audioOutput
    );
    if (newSpeakerId && newSpeakerId !== speakerId) switchDevice(newSpeakerId, 'audioOutput');

    setAudioInputsDevices(audioInputs);
    setAudioOutputsDevices(audioOutputs);
    setVideoDevices(videoInputs);
  }, [cameraId, microphoneId, speakerId, switchDevice, mediaDevices]);

  const handleClickRequestInterpreter = () => {
    toggleIsOpenSettings(false);
    if (!languageRequested && !callOutParticipant) {
      toggleIsOpenRequestInterpreter();
      return;
    }

    languageRequested && stream?.cancelCallCRCDevice(languageRequested.value, CRCProtocol.SIP);
    callOutParticipant && room?.removeUser(callOutParticipant.userId);
    setLanguageRequested(null);
    setCallOutParticipant(null);
  };

  useEffect(() => {
    if (!microphoneId) return;
    const track = ZoomVideo.createLocalAudioTrack(microphoneId);
    setAudioTrack(track);
  }, [microphoneId]);

  useEffect(() => {
    if (!isOpenSettings) {
      microphoneTester?.stop();
      microphoneTester?.destroy();
      setMicrophoneTester(undefined);
      return;
    }

    if (audioTrack && microphoneId) {
      const tester = audioTrack.testMicrophone({
        microphoneId,
        onAnalyseFrequency: setVolume
      });
      setMicrophoneTester(tester);
    }
  }, [isOpenSettings, audioTrack, microphoneId]);

  useEffect(() => {
    if (!isZoomCall || isReschedule) return;
    handleUpdatedDevices();
  }, [isZoomCall, isReschedule]);

  useEffect(() => {
    if (!Object.keys(mediaDevices).length || !(isOpenSettings && !isMobile)) return;
    handleUpdatedDevices();
  }, [mediaDevices, isMobile, isOpenSettings]);

  return {
    audioInputsDevices,
    audioOutputsDevices,
    disabledAudioButton,
    handleClickRequestInterpreter,
    handlePlayAudio,
    switchDevice,
    videoDevices,
    volume
  };
};
